import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';
import compressor from 'browser-image-compression';
import {
  collection,
  doc,
  query,
  where,
  getDocs,
  setDoc,
  deleteDoc,
} from 'firebase/firestore';
import { v4 } from 'uuid';
import Swal from 'sweetalert2';
import { DB } from '~/services/firebase';
import AWSBucket, { AWSParams } from '~/services/aws';

import Dashboard from '~/layouts/Dashboard';
import { useAuth } from '~/hooks/auth';
import { useToast } from '~/hooks/toast';
import IBanner from '~/interfaces/IBanner';
import getValidationErrors from '~/utils/getValidationErrors';

interface FormData {
  image: string;
}

const { REACT_APP_AWS_BUCKET_URL } = process.env;

const BannersForm: React.FC = () => {
  const { user, company } = useAuth();
  const { addToast } = useToast();
  // const [data, setData] = useState({} as FormData);
  const [banners, setBanners] = useState<IBanner[]>([]);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({} as any);

  const loadBanners = useCallback(async () => {
    const q = query(
      collection(DB, 'banners'),
      where('user_id', '==', user.uid),
    );
    const querySnapshot = await getDocs(q);

    const items: IBanner[] = [];

    querySnapshot.docs.forEach(item => {
      const parseItem = {
        ...item.data(),
        uid: item.id,
      } as IBanner;

      items.push(parseItem);
    });

    setBanners(items);
  }, [user]);

  useEffect(() => {
    loadBanners();
  }, [loadBanners]);

  const handleConfirmRemove = useCallback(
    async (item: IBanner) => {
      const { isConfirmed } = await Swal.fire({
        title: 'Atenção!',
        text: 'Confirme a exclusão do banner?',
        icon: 'warning',
        confirmButtonText: 'Sim',
        showCancelButton: true,
        cancelButtonText: 'Não',
      });

      if (isConfirmed) {
        const items = [...banners];
        const findIndex = items.findIndex(find => find.uid === item.uid);

        if (findIndex !== -1) {
          await deleteDoc(doc(DB, 'banners', item.uid));

          const Key = item.image.replace(`${REACT_APP_AWS_BUCKET_URL}/`, '');
          const config: any = {
            ...AWSParams,
            Key,
          };

          AWSBucket.deleteObject(config, (err, tmp) => {
            if (err) console.log(err);
          });

          items.splice(findIndex, 1);

          setBanners(items);

          addToast({
            type: 'success',
            description: 'O banner foi removido com sucesso.',
          });
        }
      }
    },
    [addToast, banners],
  );

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      try {
        const inputFile = document.getElementById('photo') as HTMLInputElement;

        if (inputFile && inputFile.files && inputFile.files.length > 0) {
          setLoading(true);
          let image = '';

          // const file = inputFile.files[0];
          // const storageRef = Storage.ref(`banners/${v4()}.png`);
          // const resposne = await storageRef.put(file);
          // image = await resposne.ref.getDownloadURL();

          const file = inputFile.files[0];
          const filename = `${company.fantasy}-${company.uid}/${v4()}.png`;

          const compressedFile = await compressor(file, {
            maxSizeMB: 1,
            maxWidthOrHeight: 500,
            useWebWorker: true,
          });

          const config: any = {
            ...AWSParams,
            Body: compressedFile,
            Key: filename,
          };

          AWSBucket.putObject(config).send();

          image = `${REACT_APP_AWS_BUCKET_URL}/${filename}`;

          const uid = v4();

          const parseData: any = {
            uid,
            user_id: user.uid,
            company_id: company.uid,
            image,
          };

          await setDoc(doc(DB, 'banners', uid), parseData);

          setTimeout(() => {
            setBanners(current => [parseData, ...current]);
          }, 500);

          addToast({
            type: 'success',
            description: 'Banner cadastrado com sucesso!',
          });

          setLoading(false);
        } else {
          setErrors({ image: 'É necessário selecionar uma imagem.' });
        }
      } catch (err: any) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const parseErrors = getValidationErrors(err);
          setErrors(parseErrors);
        }
      }
    },
    [user, company, addToast],
  );

  return (
    <Dashboard containerMessage="col-xl-8 offset-xl-2">
      <div className="row">
        <div className="col-xl-8 offset-xl-2 mb-4">
          <div className="nk-block nk-block-lg">
            <div className="nk-block-head">
              <div className="nk-block-head-content">
                {/* <div className="nk-block-head-sub">
                  <Link className="back-to" to="/categories">
                    <em className="icon ni ni-arrow-left" />
                    <span>Voltar</span>
                  </Link>
                </div> */}
                <h4 className="title nk-block-title">Adicionar Banners</h4>
                <div className="nk-block-des">
                  <p>
                    Selecione uma imagem e adicione como banner em seu
                    aplicativo.
                  </p>
                </div>
              </div>
            </div>
            <div className="card card-bordered">
              <div className="card-inner">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-sm-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Imagem</label>
                        <div className="form-control-wrap">
                          <input
                            type="file"
                            id="photo"
                            style={{ width: '100%' }}
                          />
                        </div>
                        {errors.image && (
                          <span className="invalid">{errors.image}</span>
                        )}
                      </div>
                    </div>

                    {/* <div className="col-lg-6">
                      <div className="form-group">
                        <label className="form-label">Link da Imagem</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={data.image}
                            onChange={event => {
                              setData({ ...data, image: event.target.value });
                            }}
                          />
                          {errors.image && (
                            <span className="invalid">{errors.image}</span>
                          )}
                        </div>
                      </div>
                    </div> */}

                    <div
                      className="col-sm-6 d-flex"
                      style={{ alignItems: 'flex-end' }}
                    >
                      <div className="form-group mb-3">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading && (
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            />
                          )}
                          {!loading && 'Publicar'}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-8 offset-xl-2">
          <div className="nk-block-head nk-block-head-sm">
            <div className="nk-block-between">
              <div className="nk-block-head-content">
                <h6 className="nk-block-title">Banners</h6>
              </div>
            </div>
          </div>
          <div className="nk-block">
            <div className="card card-bordered">
              <div className="card-inner-group">
                <div className="card-inner p-0">
                  <div className="nk-tb-list">
                    <div className="nk-tb-item nk-tb-head">
                      <div className="nk-tb-col">
                        <span>Imagem</span>
                      </div>
                      {/* <div className="nk-tb-col">
                        <span>Status</span>
                      </div> */}

                      <div className="nk-tb-col nk-tb-col-tools">
                        <ul className="nk-tb-actions gx-1 my-n1">
                          <li className="mr-n1" />
                        </ul>
                      </div>
                    </div>

                    {banners.map(item => (
                      <div className="nk-tb-item" key={item.uid}>
                        <div className="nk-tb-col">
                          <span className="tb-product">
                            <img
                              src={item.image}
                              alt="banner"
                              className="thumb"
                            />
                            <span className="title banner-ellipsis">
                              {item.image}
                            </span>
                          </span>
                        </div>

                        <div className="nk-tb-col nk-tb-col-tools">
                          <ul className="nk-tb-actions gx-1 my-n1">
                            <li className="mr-n1">
                              <div className="dropdown">
                                <Link
                                  to="#options"
                                  className="dropdown-toggle btn btn-icon btn-trigger"
                                  data-toggle="dropdown"
                                >
                                  <em className="icon ni ni-more-h" />
                                </Link>
                                <div className="dropdown-menu dropdown-menu-right">
                                  <ul className="link-list-opt no-bdr">
                                    <li>
                                      <Link
                                        to="#remove"
                                        onClick={() =>
                                          handleConfirmRemove(item)
                                        }
                                      >
                                        <em className="icon ni ni-trash" />
                                        <span>Excluir</span>
                                      </Link>
                                    </li>
                                  </ul>
                                </div>
                              </div>
                            </li>
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dashboard>
  );
};

export default BannersForm;
